import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { loginMemberStep1, loginMemberVerify2FA } from '@/lib/memberAuth';
import {
  useMemberHelpLine,
  MemberJoinError,
  isValidUsPhone,
  loginPhoneStart,
  loginPhoneVerify,
  normalizeUsPhone,
} from '@/lib/memberJoin';
import {
  AlertCircle,
  Lock,
  Mail,
  LogIn,
  Smartphone,
  Apple,
  Monitor,
  Download,
  Shield,
  CheckCircle
} from 'lucide-react';

import { useAppTranslation } from '@/contexts/TranslationContext';
import { AUTH_BACKDROP } from '@/src/platform/shared/components/authBackdrop';
import {
  DESKTOP_MACOS_PUBLISHED,
  DESKTOP_WINDOWS_PUBLISHED,
  usePublishedDesktopVersion,
  macDownloadUrl,
  windowsDownloadUrl,
  MOBILE_IOS_PUBLISHED,
  MOBILE_ANDROID_PUBLISHED,
  IOS_APP_URL,
  ANDROID_APP_URL,
} from '@/lib/desktopDownloads';
export default function MemberLoginPage() {
  const { t } = useAppTranslation();
  const helpLine = useMemberHelpLine();
  const navigate = useNavigate();
  // Live published desktop version (from latest.json); null until deployed + CI-passed.
  const desktopVersion = usePublishedDesktopVersion();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [show2FA, setShow2FA] = useState(false);
  const [maskedPhone, setMaskedPhone] = useState('');
  // TRUS-778: a text code is the default way in. People who joined through
  // /member/join have no password at all.
  const [mode, setMode] = useState<'phone' | 'email'>('phone');
  const [phone, setPhone] = useState('');
  const [phoneLoginId, setPhoneLoginId] = useState('');
  const [phoneMasked, setPhoneMasked] = useState('');

  const switchMode = (next: 'phone' | 'email') => {
    setMode(next);
    setError('');
    setVerificationCode('');
    setShow2FA(false);
    setPhoneLoginId('');
  };

  const phoneError = (err: unknown, fallback: string): string => {
    if (err instanceof MemberJoinError) {
      if (err.status === 429) return t('login.too_many_tries', 'Too many tries. Please wait a minute and try again.');
      if (err.message) return err.message;
      if (!helpLine) {
        return t('login.phone_unavailable_no_phone', 'We could not sign you in with a text code just now. Try email and password instead.');
      }
      return t(
        'login.phone_unavailable',
        'We could not sign you in with a text code just now. Try email and password, or text or call {{phone}}.',
        { phone: helpLine.display },
      );
    }
    return fallback;
  };

  const handlePhoneStart = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!isValidUsPhone(phone)) {
      setError(t('login.phone_invalid', 'Enter a 10-digit US mobile number.'));
      return;
    }
    setLoading(true);
    try {
      const res = await loginPhoneStart(`+1${normalizeUsPhone(phone)}`);
      setPhoneLoginId(res.login_id);
      setPhoneMasked(res.phone_masked || '');
      setVerificationCode('');
    } catch (err) {
      setError(phoneError(err, t('login.could_not_send', 'We could not send a code. Please try again.')));
    } finally {
      setLoading(false);
    }
  };

  const handlePhoneVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await loginPhoneVerify(phoneLoginId, verificationCode);
      navigate('/member/home');
    } catch (err) {
      setError(phoneError(err, t('login.code_failed', 'That code did not work. Check it and try again.')));
    } finally {
      setLoading(false);
    }
  };

  const newHereLinks = (
    <div className="flex flex-col sm:flex-row items-center justify-center gap-x-4 gap-y-1 text-sm">
      <Link to="/member/join" className="inline-flex items-center min-h-[44px] text-v2accent-ink hover:underline font-medium">
        {t('login.new_here_get_help', 'New here? Get help')}
      </Link>
      <Link to="/member/register" className="inline-flex items-center min-h-[44px] text-v2accent-ink hover:underline font-medium">
        {t('login.i_have_invite_code', 'I have an invite code')}
      </Link>
    </div>
  );

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Step 1: Send credentials. Either 2FA is required (show the code form)
      // or it's skipped (admin-exempt / disabled) and step1 already returned a
      // full session — in which case go straight to the dashboard.
      const result = await loginMemberStep1(email, password);

      if (result.requires_2fa) {
        setMaskedPhone(result.phone_number_masked || '');
        setShow2FA(true);
      } else if (result.token) {
        navigate('/member/dashboard');
      } else {
        setError('Login failed. Please try again.');
      }
    } catch (err: any) {
      setError(err.message || 'Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify2FA = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Step 2: Verify 2FA code and get token
      await loginMemberVerify2FA(email, verificationCode);

      // Redirect to dashboard
      navigate('/member/dashboard');
    } catch (err: any) {
      setError(err.message || 'Verification failed. Please check your code.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={AUTH_BACKDROP}>
      {/* Header */}
      <header className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/member/home" className="flex items-center">
              <img src="/weavein-wordmark.png" alt="WeaveIn" className="h-8 lg:h-10 xl:h-11 w-auto shrink-0" />
            </Link>
            <div className="flex items-center space-x-4">
              <Link to="/login" className="text-sm text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200">
                Staff Login
              </Link>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 py-8 lg:py-12">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-start">
          {/* Login Form */}
          <Card className="w-full shadow-lg">
        <CardHeader className="space-y-1">
          <div className="flex items-center justify-center mb-4">
            <div className="w-12 h-12 rounded-full bg-v2accent flex items-center justify-center">
              <Lock className="w-6 h-6 text-white" />
            </div>
          </div>
          <CardTitle className="text-2xl font-bold text-center">
            {t('login.member_portal', 'Member Portal')}
          </CardTitle>
          <CardDescription className="text-center">
            {t('login.sign_in_to_access_your', 'Sign in to access your portal')}
          </CardDescription>
        </CardHeader>

        {mode === 'phone' ? (
          !phoneLoginId ? (
            <form onSubmit={handlePhoneStart}>
              <CardContent className="space-y-4">
                {error && (
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}
                <div className="space-y-2">
                  <Label htmlFor="login-phone">{t('login.mobile_number', 'Mobile number')}</Label>
                  <div className="relative">
                    <Smartphone className="absolute left-3 top-3.5 h-4 w-4 text-ink-400" />
                    <Input
                      id="login-phone"
                      type="tel"
                      inputMode="tel"
                      autoComplete="tel-national"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      required
                      disabled={loading}
                      className="pl-10 min-h-[48px]"
                    />
                  </div>
                  <p className="text-sm text-ink-500">
                    {t('login.we_text_you_a_code', 'We text you a 6-digit code. No password needed.')}
                  </p>
                </div>
              </CardContent>
              <CardFooter className="flex flex-col space-y-4">
                <Button
                  type="submit"
                  className="w-full min-h-[48px] bg-v2accent hover:bg-v2accent/90"
                  disabled={loading}
                >
                  {loading ? t('login.sending_code', 'Sending code...') : t('login.text_me_a_code', 'Text me a code')}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  className="w-full min-h-[44px]"
                  onClick={() => switchMode('email')}
                  disabled={loading}
                >
                  <Mail className="w-4 h-4 mr-2" />
                  {t('login.use_email_password_instead', 'Use email and password instead')}
                </Button>
                {newHereLinks}
              </CardFooter>
            </form>
          ) : (
            <form onSubmit={handlePhoneVerify}>
              <CardContent className="space-y-4">
                {error && (
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}
                <p className="text-sm text-ink-700 text-center">
                  {t('login.code_sent_to', 'We sent a 6-digit code to {{phone}}.', { phone: phoneMasked || phone })}
                </p>
                <div className="space-y-2">
                  <Label htmlFor="login-phone-code">{t('login.verification_code', 'Verification Code')}</Label>
                  <Input
                    id="login-phone-code"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    value={verificationCode}
                    onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    required
                    disabled={loading}
                    maxLength={6}
                    className="text-center text-2xl tracking-widest font-mono min-h-[52px]"
                    autoFocus
                  />
                </div>
              </CardContent>
              <CardFooter className="flex flex-col space-y-3">
                <Button
                  type="submit"
                  className="w-full min-h-[48px] bg-v2accent hover:bg-v2accent/90"
                  disabled={loading || verificationCode.length !== 6}
                >
                  {loading ? t('login.verifying', 'Verifying...') : t('login.sign_in', 'Sign In')}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  className="w-full min-h-[44px]"
                  onClick={() => {
                    setPhoneLoginId('');
                    setVerificationCode('');
                    setError('');
                  }}
                  disabled={loading}
                >
                  {t('login.change_number_or_resend', 'Change number or send a new code')}
                </Button>
              </CardFooter>
            </form>
          )
        ) : (!show2FA ? (
          <form onSubmit={handleLogin}>
            <CardContent className="space-y-4">
              {error && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <div className="space-y-2">
                <Label htmlFor="email">{t('login.email', 'Email')}</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-3 h-4 w-4 text-gray-400 dark:text-gray-500" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="your.email@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    disabled={loading}
                    className="pl-10"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password">{t('login.password', 'Password')}</Label>
                  <Link
                    to="/member/forgot-password"
                    className="text-sm text-v2accent-ink hover:underline"
                  >
                    {t('login.forgot_password', 'Forgot password?')}
                  </Link>
                </div>
                <div className="relative">
                  <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-400 dark:text-gray-500" />
                  <Input
                    id="password"
                    type="password"
                    placeholder={t('login.enter_your_password', 'Enter your password')}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    disabled={loading}
                    className="pl-10"
                    autoComplete="current-password"
                  />
                </div>
              </div>
            </CardContent>

            <CardFooter className="flex flex-col space-y-4">
              <Button
                type="submit"
                className="w-full bg-v2accent hover:bg-v2accent/90"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                    {t('login.sending_code', 'Sending code...')}
                  </>
                ) : (
                  <>
                    <LogIn className="w-4 h-4 mr-2" />
                    {t('login.sign_in', 'Sign In')}
                  </>
                )}
              </Button>

              <Button
                type="button"
                variant="ghost"
                className="w-full min-h-[44px]"
                onClick={() => switchMode('phone')}
                disabled={loading}
              >
                {t('login.use_text_code_instead', 'Sign in with a text code instead')}
              </Button>
              {newHereLinks}
            </CardFooter>
          </form>
        ) : (
          <form onSubmit={handleVerify2FA}>
            <CardContent className="space-y-4">
              {error && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <div className="text-center mb-4">
                <div className="w-16 h-16 rounded-full bg-v2accent-soft flex items-center justify-center mx-auto mb-3">
                  <Smartphone className="w-8 h-8 text-v2accent" />
                </div>
                <h3 className="text-lg font-semibold mb-2">{t('login.verify_your_identity', 'Verify Your Identity')}</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  We've sent a 6-digit verification code to {maskedPhone}
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="code">{t('login.verification_code', 'Verification Code')}</Label>
                <Input
                  id="code"
                  type="text"
                  placeholder={t('login.enter_6digit_code', 'Enter 6-digit code')}
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value)}
                  required
                  disabled={loading}
                  maxLength={6}
                  className="text-center text-2xl tracking-widest font-mono"
                  autoFocus
                />
              </div>
            </CardContent>

            <CardFooter className="flex flex-col space-y-4">
              <Button
                type="submit"
                className="w-full bg-v2accent hover:bg-v2accent/90"
                disabled={loading || verificationCode.length !== 6}
              >
                {loading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                    {t('login.verifying', 'Verifying...')}
                  </>
                ) : (
                  'Verify & Sign In'
                )}
              </Button>

              <Button
                type="button"
                variant="ghost"
                className="w-full"
                onClick={() => {
                  setShow2FA(false);
                  setVerificationCode('');
                  setError('');
                }}
                disabled={loading}
              >
                {t('login.back_to_login', 'Back to login')}
              </Button>
            </CardFooter>
          </form>
        ))}
      </Card>

          {/* App Downloads Section */}
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                {t('login.get_the_app', 'Get the WeaveIn App')}
              </h2>
              <p className="text-gray-600 dark:text-gray-400">
                {t('login.access_on_device', 'Access your care portal on your preferred device')}
              </p>
            </div>

            {/* Desktop Apps */}
            <Card className="dark:bg-gray-800 dark:border-gray-700">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center">
                  <Monitor className="w-5 h-5 mr-2 text-v2accent" />
                  {t('login.desktop_apps', 'Desktop Apps')}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {/* macOS — available once the signed/notarized build is published.
                    Gated on the flag only (NOT the live manifest fetch) so an
                    ad-blocker/network hiccup fetching latest.json can't demote a
                    shipped app to "Coming Soon". Version shown only when it resolves. */}
                {DESKTOP_MACOS_PUBLISHED ? (
                  <a
                    href={macDownloadUrl(desktopVersion)}
                    className="flex items-center justify-between p-3 rounded-lg border border-gray-200 dark:border-gray-600 hover:border-v2accent hover:bg-v2accent-soft transition-colors group"
                  >
                    <div className="flex items-center space-x-3">
                      <Apple className="w-8 h-8 text-gray-700 dark:text-gray-300" />
                      <div>
                        <p className="font-medium text-gray-900 dark:text-white">macOS</p>
                        <p className="text-sm text-gray-500 dark:text-gray-400">{t('login.download_mac', 'Download for Mac')}{desktopVersion ? ` • v${desktopVersion}` : ''}</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Badge variant="accent">{t('login.available', 'Available')}</Badge>
                      <Download className="w-5 h-5 text-gray-400 group-hover:text-v2accent" />
                    </div>
                  </a>
                ) : (
                  <div className="flex items-center justify-between p-3 rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/50">
                    <div className="flex items-center space-x-3">
                      <Apple className="w-8 h-8 text-gray-700 dark:text-gray-300" />
                      <div>
                        <p className="font-medium text-gray-900 dark:text-white">macOS</p>
                        <p className="text-sm text-gray-500 dark:text-gray-400">{t('login.desktop_app', 'Desktop app')}</p>
                      </div>
                    </div>
                    <Badge variant="muted">{t('login.coming_soon', 'Coming Soon')}</Badge>
                  </div>
                )}

                {/* Windows — link only once a signed build is published */}
                {DESKTOP_WINDOWS_PUBLISHED ? (
                  <a
                    href={windowsDownloadUrl(desktopVersion)}
                    className="flex items-center justify-between p-3 rounded-lg border border-gray-200 dark:border-gray-600 hover:border-v2accent hover:bg-v2accent-soft transition-colors group"
                  >
                    <div className="flex items-center space-x-3">
                      <Monitor className="w-8 h-8 text-gray-700 dark:text-gray-300" />
                      <div>
                        <p className="font-medium text-gray-900 dark:text-white">Windows</p>
                        <p className="text-sm text-gray-500 dark:text-gray-400">{t('login.download_pc', 'Download for PC')}{desktopVersion ? ` • v${desktopVersion}` : ''}</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Badge variant="accent">{t('login.available', 'Available')}</Badge>
                      <Download className="w-5 h-5 text-gray-400 group-hover:text-v2accent" />
                    </div>
                  </a>
                ) : (
                  <div className="flex items-center justify-between p-3 rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/50">
                    <div className="flex items-center space-x-3">
                      <Monitor className="w-8 h-8 text-gray-700 dark:text-gray-300" />
                      <div>
                        <p className="font-medium text-gray-900 dark:text-white">Windows</p>
                        <p className="text-sm text-gray-500 dark:text-gray-400">{t('login.download_pc', 'Download for PC')}</p>
                      </div>
                    </div>
                    <Badge variant="muted">{t('login.coming_soon', 'Coming Soon')}</Badge>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Mobile Apps */}
            <Card className="dark:bg-gray-800 dark:border-gray-700">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center">
                  <Smartphone className="w-5 h-5 mr-2 text-v2accent" />
                  {t('login.mobile_apps', 'Mobile Apps')}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {MOBILE_IOS_PUBLISHED ? (
                  <a
                    href={IOS_APP_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-between p-3 rounded-lg border border-gray-200 dark:border-gray-600 hover:border-v2accent hover:bg-v2accent-soft transition-colors group"
                  >
                    <div className="flex items-center space-x-3">
                      <Apple className="w-8 h-8 text-gray-700 dark:text-gray-300" />
                      <div>
                        <p className="font-medium text-ink">iOS App</p>
                        <p className="text-sm text-gray-500 dark:text-gray-400">iPhone & iPad</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Badge variant="accent">{t('login.available', 'Available')}</Badge>
                      <Download className="w-5 h-5 text-gray-400 group-hover:text-v2accent" />
                    </div>
                  </a>
                ) : (
                  <div className="flex items-center justify-between p-3 rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/50">
                    <div className="flex items-center space-x-3">
                      <Apple className="w-8 h-8 text-gray-700 dark:text-gray-300" />
                      <div>
                        <p className="font-medium text-ink">iOS App</p>
                        <p className="text-sm text-gray-500 dark:text-gray-400">iPhone & iPad</p>
                      </div>
                    </div>
                    <Badge variant="muted">{t('login.coming_soon', 'Coming Soon')}</Badge>
                  </div>
                )}

                {MOBILE_ANDROID_PUBLISHED ? (
                  <a
                    href={ANDROID_APP_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-between p-3 rounded-lg border border-gray-200 dark:border-gray-600 hover:border-v2accent hover:bg-v2accent-soft transition-colors group"
                  >
                    <div className="flex items-center space-x-3">
                      <Smartphone className="w-8 h-8 text-gray-700 dark:text-gray-300" />
                      <div>
                        <p className="font-medium text-ink">Android App</p>
                        <p className="text-sm text-gray-500 dark:text-gray-400">Phone & Tablet</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Badge variant="accent">{t('login.available', 'Available')}</Badge>
                      <Download className="w-5 h-5 text-gray-400 group-hover:text-v2accent" />
                    </div>
                  </a>
                ) : (
                  <div className="flex items-center justify-between p-3 rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/50">
                    <div className="flex items-center space-x-3">
                      <Smartphone className="w-8 h-8 text-gray-700 dark:text-gray-300" />
                      <div>
                        <p className="font-medium text-ink">Android App</p>
                        <p className="text-sm text-gray-500 dark:text-gray-400">Phone & Tablet</p>
                      </div>
                    </div>
                    <Badge variant="muted">{t('login.coming_soon', 'Coming Soon')}</Badge>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Web App - Current Platform */}
            <Card className="bg-surface border-v2border">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 rounded-full bg-v2accent flex items-center justify-center">
                      <CheckCircle className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900 dark:text-white">{t('login.web_app', 'Web App')}</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">{t('login.using_web_app', "You're currently using the web app")}</p>
                    </div>
                  </div>
                  <Badge variant="accent">{t('login.active', 'Active')}</Badge>
                </div>
              </CardContent>
            </Card>

            {/* Security Info */}
            <Card className="bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center">
                  <Shield className="w-4 h-4 mr-2 text-green-600" />
                  {t('login.security', 'Security & Privacy')}
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="grid grid-cols-2 gap-3">
                  <div className="flex items-center space-x-2 text-xs text-gray-600 dark:text-gray-400">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    <span>HIPAA Compliant</span>
                  </div>
                  <div className="flex items-center space-x-2 text-xs text-gray-600 dark:text-gray-400">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    <span>End-to-End Encryption</span>
                  </div>
                  <div className="flex items-center space-x-2 text-xs text-gray-600 dark:text-gray-400">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    <span>2FA Authentication</span>
                  </div>
                  <div className="flex items-center space-x-2 text-xs text-gray-600 dark:text-gray-400">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    <span>PHI Protected</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Help */}
            <div className="text-center text-sm text-gray-500 dark:text-gray-400">
              <p>{t('login.need_help', 'Need help?')} <a href="mailto:support@weavein.ai" className="text-v2accent-ink hover:underline">support@weavein.ai</a></p>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 mt-12">
        <div className="max-w-6xl mx-auto px-4 py-6">
          <div className="flex flex-col md:flex-row items-center justify-between space-y-4 md:space-y-0">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              © {new Date().getFullYear()} WeaveIn.ai by Trust. All rights reserved.
            </p>
            <div className="flex items-center space-x-6 text-sm">
              <Link to="/consent/terms-of-use" className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200">{t('login.terms', 'Terms of Use')}</Link>
              <Link to="/consent/hipaa-authorization" className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200">HIPAA</Link>
              <Link to="/consent/privacy-policy" className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200">{t('login.privacy', 'Privacy Policy')}</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
